/**
 * The personal page — FR-STAT-18.
 *
 * It used to be one long scroll called "Your account": who you are signed in
 * as, a password form, and — if you were an admin — the whole club roster and
 * guest management underneath. Statistics were not on it at all. A member
 * looking for their own numbers went to a club-wide table and found their row
 * among twenty, which is a strange way to answer "how am I doing".
 *
 * So the page now serves three separate purposes and says which one you are
 * looking at:
 *
 *   Stats    your own figures, and any partnership you are part of
 *   Account  who you are, your password, the backup tracker, sign out
 *   Admin    the roster and the guests — only for admins, who are the only
 *            people who can act on them
 *
 * Admin is absent rather than disabled for a member. A control somebody cannot
 * use is worse than one they cannot see: it invites a tap and then explains
 * why the tap was pointless.
 */

import { useEffect, useState } from "react";
import { api, type Me, type Player } from "../api.js";
import { PairProfile, PlayerProfile } from "./PlayerProfile.js";
import { Settings } from "./Settings.js";
import { ClubAdmin } from "./ClubAdmin.js";

type Section = "stats" | "account" | "admin";

export function YouScreen({
  me,
  onSignedOut,
  onOpenSession,
  onShowGuide,
}: {
  me: Me;
  onSignedOut: () => void;
  onOpenSession?: (sessionId: string) => void;
  onShowGuide?: () => void;
}): React.JSX.Element {
  const [section, setSection] = useState<Section>("stats");

  const sections: Array<{ id: Section; label: string }> = [
    { id: "stats", label: "Stats" },
    { id: "account", label: "Account" },
    ...(me.role === "admin" ? [{ id: "admin" as const, label: "Admin" }] : []),
  ];

  return (
    <>
      <div className="row subnav">
        {sections.map((s) => (
          <button
            key={s.id}
            className={s.id === section ? "chip chip-on" : "chip"}
            aria-current={s.id === section ? "page" : undefined}
            onClick={() => setSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === "stats" ? <YourStats me={me} /> : null}
      {section === "account" ? (
        <Settings me={me} onSignedOut={onSignedOut} onShowGuide={onShowGuide} />
      ) : null}
      {section === "admin" && me.role === "admin" ? (
        <ClubAdmin onOpenSession={onOpenSession} />
      ) : null}
    </>
  );
}

/**
 * Your own figures, and the partnership you are half of.
 *
 * Both are the existing profile components rather than a second rendering of
 * the same statistics (FR-STAT-6): a pair record shown here and a pair record
 * shown on the pair page must be the same numbers, and the cheapest way to
 * guarantee that is for there to be one component.
 */
function YourStats({ me }: { me: Me }): React.JSX.Element {
  const [partner, setPartner] = useState<string>("");
  const [players, setPlayers] = useState<Player[]>([]);

  useEffect(() => {
    api
      .players()
      .then((list) => setPlayers(list.filter((p) => p.id !== me.id)))
      .catch(() => undefined);
  }, [me.id]);

  if (partner) {
    return (
      <PairProfile playerA={me.id} playerB={partner} onClose={() => setPartner("")} />
    );
  }

  return (
    <>
      {/* No `onClose`: this is the screen, not something opened on top of it. */}
      <PlayerProfile playerId={me.id} onOpenPair={(_, b) => setPartner(b)} />

      <section className="block">
        <h3 className="card-head">With a partner</h3>
        <p className="muted small">
          Pick anybody you have played alongside to see how the two of you do
          together.
        </p>
        <label>
          Partner
          <select
            value={partner}
            aria-label="Partner"
            onChange={(e) => setPartner(e.target.value)}
          >
            <option value="">Choose a player…</option>
            {players.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
                {p.isGuest ? " (guest)" : ""}
              </option>
            ))}
          </select>
        </label>
      </section>
    </>
  );
}
